/**
 * The pipeline as a grid - the table alternative to the board.
 *
 * It takes the same stage groups the board renders, so the two views always
 * agree about which requests sit where. A tab per stage, plus All, picks
 * which of them the grid holds; the tab is kept with the grid's saved layout,
 * so the table opens on the stage somebody was working last time, on any
 * device. Unsorted, All reads stage by stage, newest first within each - the
 * order the grouped table it replaces had.
 *
 * Every column the grouped table showed is here - who asked, how to reach
 * them, where they came from, when, what was last done and who has it - and
 * the facts that used to be chips or captions (campaign, landing page, what
 * they wanted, city) are columns of their own, hidden until wanted, so they
 * can be filtered, sorted and exported rather than only read. A row opens
 * the read-only view; the tick wins it (after asking), the eye views it and
 * the pencil goes straight to the editor.
 */
import { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../store/AppContext.jsx';
import { parseDate, formatDate, daysAgo } from '../lib/dates.js';
import Icon from '../lib/icons.jsx';
import { OwnerPill, Dash, StagePill, EmptyState } from './Primitives.jsx';
import PlanChip from './PlanChip.jsx';
import DataGrid from './grid/DataGrid.jsx';
import useGridPrefs from './grid/useGridPrefs.js';
import { exportAllowed } from './grid/exportGrid.js';
import {
  ContactCell, CompanyCell, SourceCell, EmailCell, ScorePill, aiScoreOf, sinceDay
} from './CrmCells.jsx';
import {
  isStalled, companyName, leadDetails, lastTouch, clientOfDeal, dealActivities, boardRows,
  displayStage, subStatus
} from '../domain/crm.js';

/**
 * One row of the grid: the lead, and everything its cells read, worked out
 * once per row rather than once per cell per render.
 */
function leadRow(S, crm, rows, d) {
  const f = leadDetails(crm, d);
  const requested = parseDate(f.requested);
  const last = dealActivities(crm, d.id)[0] || null;
  const arrived = requested || parseDate(d.created);
  return {
    d,
    f,
    requested,
    last,
    lastAt: last ? parseDate(last.due) : null,
    touch: lastTouch(crm, d.id),
    stale: isStalled(S, crm, d),
    client: clientOfDeal(rows, d) || null,
    company: companyName(crm, d.cid),
    place: [f.city, f.country].filter(Boolean).join(', '),
    age: arrived ? Math.max(0, daysAgo(arrived)) : undefined,
    added: parseDate(d.created),
    score: aiScoreOf(d)
  };
}

/* What a first visit sees. The rest is a click away in Columns. The first two
   columns — the request and its stage — are frozen so they stay in view while
   the rest scroll sideways. */
const LAYOUT = {
  tab: 'All',
  hidden: [
    'stalled', 'age', 'value', 'next', 'email', 'phone', 'city', 'country',
    'campaign', 'products', 'landing'
  ],
  pageSize: 25,
  frozen: 3
};

function pipelineColumns({ stages, dateFmt, fmtMoney, act, withScores }) {
  const day = d => formatDate(d, false, dateFmt);
  const cols = [
    {
      /* A plain serial, in the order the table is shown — the row number people
         read a list by, not a stored id. */
      id: 'sno',
      size: 64,
      enableSorting: false,
      accessorFn: r => r.sno,
      meta: { label: '#', filter: false, search: false, align: 'center' },
      cell: ({ getValue }) => <span className="dim">{getValue()}</span>
    },
    {
      /* When the lead was created — the first column after the serial, shown by
         default. For a tracked lead this is when the row reached the app. */
      id: 'created',
      size: 140,
      accessorFn: r => r.added || undefined,
      meta: { label: 'Created on', type: 'date' },
      cell: ({ getValue }) => (getValue() ? day(getValue()) : <Dash />)
    },
    {
      id: 'title',
      /* Wide enough for the name and a line of chips without wrapping. */
      size: 340,
      minSize: 180,
      accessorFn: r => r.d.title || undefined,
      meta: {
        label: 'Request',
        type: 'text',
        searchValue: r => `${r.d.title || ''} ${r.f.products || ''} ${r.f.productList.join(' ')}`
      },
      cell: ({ row: { original: r } }) => (
        <div className="cname">
          <div className="lreq">
            <b title={r.d.title}>{r.d.title}</b>
            <div className="lbadges">
              {/* Already on the books: the one fact that changes what this
                  row is for. */}
              {r.client ? <span className="dealchip cli"><Icon name="check" />Client</span> : null}
              {/* Typed in rather than tracked, which is why it may carry none
                  of the detail the others do. */}
              {!r.d.origin ? <span className="dealchip man"><Icon name="edit" />Manual</span> : null}
              {r.f.productList.length || r.f.products
                ? <PlanChip list={r.f.productList} label={r.f.products} />
                : <span className="dealchip">Demo request</span>}
              {r.d.cycle ? <span className="dealchip">{r.d.cycle}</span> : null}
              {r.f.landing ? (
                <a className="lbadge land" href={r.f.landing} target="_blank" rel="noreferrer" title={r.f.landing}>
                  <Icon name="link" />{r.f.landingPath}
                </a>
              ) : null}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'stage',
      size: 160,
      accessorFn: r => displayStage(r.d) || undefined,
      meta: {
        label: 'Status',
        type: 'enum',
        options: stages,
        searchValue: r => `${displayStage(r.d)} ${subStatus(r.d)}`
      },
      cell: ({ row: { original: r } }) => (
        <div className="lstage">
          <StagePill value={displayStage(r.d)} />
          {subStatus(r.d) ? <small>{subStatus(r.d)}</small> : null}
        </div>
      )
    },
    {
      id: 'contact',
      size: 240,
      accessorFn: r => r.f.email || r.f.phone || undefined,
      meta: {
        label: 'Contact',
        type: 'text',
        exportValue: r => [r.f.email, r.f.phone].filter(Boolean).join(' · ')
      },
      cell: ({ row }) => <ContactCell f={row.original.f} />
    },
    {
      id: 'company',
      size: 210,
      accessorFn: r => r.company || undefined,
      meta: { label: 'Company', type: 'text', searchValue: r => `${r.company} ${r.place}` },
      cell: ({ row }) => <CompanyCell company={row.original.company} place={row.original.place} />
    },
    {
      id: 'source',
      size: 160,
      accessorFn: r => r.d.source || undefined,
      meta: { label: 'Source', type: 'enum', searchValue: r => `${r.d.source || ''} ${r.f.campaign}` },
      cell: ({ row }) => <SourceCell source={row.original.d.source} campaign={row.original.f.campaign} />
    },
    {
      id: 'requested',
      size: 150,
      accessorFn: r => r.requested || undefined,
      meta: { label: 'Requested', type: 'date' },
      cell: ({ row: { original: r } }) => (r.requested ? (
        <div className="lcontact">
          <span className="datep"><Icon name="cal" />{day(r.requested)}</span>
          <small>{sinceDay(r.requested)}</small>
        </div>
      ) : <Dash />)
    },
    {
      /* What was last done to this lead - the thing a board is read for.
         Sorted and filtered by when; searched and exported as what. */
      id: 'lastActivity',
      size: 250,
      accessorFn: r => r.lastAt || undefined,
      meta: {
        label: 'Last activity',
        type: 'date',
        exportType: 'text',
        exportValue: r => (r.last
          ? `${r.lastAt ? `${day(r.lastAt)} - ` : ''}${r.last.type}: ${r.last.subject}${r.last.who ? ` (${r.last.who})` : ''}${r.last.done ? '' : ', still open'}`
          : ''),
        searchValue: r => (r.last ? `${r.last.type} ${r.last.subject} ${r.last.who || ''} ${r.lastAt ? day(r.lastAt) : ''}` : '')
      },
      cell: ({ row: { original: r } }) => (r.last ? (
        <div className="llast">
          <b title={r.last.subject}><span className="lltype">{r.last.type}</span>{r.last.subject}</b>
          <small>
            {day(r.lastAt)}
            {r.last.who ? ` · ${r.last.who}` : ''}
            {r.last.done ? '' : ' · still open'}
          </small>
        </div>
      ) : null)
    },
    {
      id: 'owner',
      size: 150,
      accessorFn: r => r.d.owner || undefined,
      meta: { label: 'Owner', type: 'enum', blank: 'Unowned' },
      cell: ({ getValue }) => <OwnerPill name={getValue()} />
    },
    {
      /* How long it has been quiet - red once it counts as stalled, which
         is what decides whether the owner matters. */
      id: 'lastTouch',
      size: 150,
      accessorFn: r => r.touch || undefined,
      meta: { label: 'Last touch', type: 'date', blank: 'Never touched' },
      cell: ({ row: { original: r } }) => (
        <span
          className={`datep${r.stale ? ' bad' : ''}`}
          title={r.touch ? `Last note ${day(r.touch)}${r.stale ? ' — stalled' : ''}` : 'Nothing logged yet'}
        >
          <Icon name="clock" />
          {r.touch ? `${daysAgo(r.touch)}d since a note` : 'never touched'}
        </span>
      )
    },
    {
      id: 'stalled',
      size: 120,
      accessorFn: r => (r.stale ? 'Stalled' : 'On track'),
      meta: { label: 'Stalled', type: 'enum', options: ['Stalled', 'On track'] },
      cell: ({ getValue }) => (getValue() === 'Stalled'
        ? <span className="dealchip bad"><Icon name="alert" />Stalled</span>
        : <span className="dim">On track</span>)
    },
    {
      id: 'age',
      size: 110,
      accessorFn: r => r.age,
      meta: { label: 'Age (days)', type: 'number' },
      cell: ({ getValue }) => (getValue() === undefined ? <Dash /> : `${getValue()}d`)
    },
    {
      id: 'value',
      size: 130,
      accessorFn: r => (+r.d.value > 0 ? +r.d.value : undefined),
      meta: { label: 'Value', type: 'number' },
      cell: ({ getValue }) => (getValue() === undefined ? <Dash /> : <b>{fmtMoney(getValue())}</b>)
    },
    {
      id: 'next',
      size: 220,
      accessorFn: r => r.d.next || undefined,
      meta: { label: 'Next step', type: 'text' },
      cell: ({ getValue }) => (getValue() ? <span title={getValue()}>{getValue()}</span> : <Dash />)
    },
    {
      id: 'email',
      size: 220,
      accessorFn: r => r.f.email || undefined,
      meta: { label: 'Email', type: 'text' },
      cell: ({ getValue }) => <EmailCell email={getValue()} />
    },
    {
      id: 'phone',
      size: 150,
      accessorFn: r => r.f.phone || undefined,
      meta: { label: 'Phone', type: 'text' }
    },
    {
      id: 'city',
      size: 140,
      accessorFn: r => r.f.city || undefined,
      meta: { label: 'City', type: 'enum' }
    },
    {
      id: 'country',
      size: 170,
      accessorFn: r => r.f.country || undefined,
      meta: { label: 'Country', type: 'enum' }
    },
    {
      id: 'campaign',
      size: 180,
      accessorFn: r => r.f.campaign || undefined,
      meta: { label: 'Campaign', type: 'enum' }
    },
    {
      id: 'products',
      size: 200,
      accessorFn: r => r.f.productList.join(', ') || r.f.products || undefined,
      meta: { label: 'Wants', type: 'text' },
      cell: ({ row: { original: r } }) => (r.f.productList.length || r.f.products
        ? <PlanChip list={r.f.productList} label={r.f.products} />
        : <Dash />)
    },
    {
      id: 'landing',
      size: 200,
      accessorFn: r => r.f.landingPath || undefined,
      meta: { label: 'Landing page', type: 'text', exportValue: r => r.f.landing },
      cell: ({ row: { original: r } }) => (r.f.landing ? (
        <a className="lbadge land" href={r.f.landing} target="_blank" rel="noreferrer" title={r.f.landing}>
          <Icon name="link" />{r.f.landingPath}
        </a>
      ) : <Dash />)
    },
  ];

  /* Only once something has been scored: a column that is a dash on every
     row is a column about a feature, not about the leads. */
  if (withScores) {
    cols.push({
      id: 'ai',
      size: 110,
      accessorFn: r => r.score,
      meta: { label: 'AI score', type: 'number', search: false },
      cell: ({ row: { original: r } }) => <ScorePill score={r.score} why={r.d.aiWhy} />
    });
  }

  cols.push({
    id: 'actions',
    size: 124,
    enableSorting: false,
    meta: { label: 'Actions', pin: 'right', export: false, filter: false, search: false },
    cell: ({ row: { original: r } }) => (
      <div className="acts">
        <button
          type="button"
          className="won"
          title="Mark as won — moves to Deals"
          onClick={() => act.current.win(r.d)}
        >
          <Icon name="check" />
        </button>
        <button type="button" className="vw" title="View details" onClick={() => act.current.view(r.d)}>
          <Icon name="eye" />
        </button>
        <button type="button" title="Edit" onClick={() => act.current.edit(r.d)}>
          <Icon name="edit" />
        </button>
      </div>
    )
  });

  return cols;
}

export default function PipelineGrid({ groups, hideTabs = false }) {
  const { S, crm, rows, setSheet, moveDealStage, fmtMoney, me, menus } = useApp();
  const navigate = useNavigate();
  const layout = useGridPrefs('grid:pipeline', LAYOUT);

  /* A saved tab whose stage has since been renamed away falls back to All
     rather than to an empty table nobody can leave. When the stage is chosen
     outside the grid (the stage tiles above it), the grid holds everything it
     is handed and shows no tabs of its own. */
  const stages = groups.map(g => g.stage);
  const tab = hideTabs ? 'All' : (stages.includes(layout.prefs.tab) ? layout.prefs.tab : 'All');

  const list = useMemo(() => (tab === 'All'
    ? groups.flatMap(g => g.deals)
    : (groups.find(g => g.stage === tab) || { deals: [] }).deals), [groups, tab]);

  const data = useMemo(
    () => list.map((d, i) => ({ ...leadRow(S, crm, rows, d), sno: i + 1 })),
    [list, S, crm, rows]
  );

  /* The buttons reach the store through a ref, so the column definitions do
     not have to be rebuilt - and every cell redrawn - when it changes. */
  const act = useRef(null);
  act.current = {
    win: d => {
      // eslint-disable-next-line no-alert
      if (window.confirm(`Mark “${d.title}” as won? It moves to Deals.`)) moveDealStage(d.id, 'Won');
    },
    /* The lead opens as its own full page (brief §7), not a panel. */
    view: d => navigate(`/leads/${d.id}`),
    edit: d => setSheet({ kind: 'deal', id: d.id })
  };

  const withScores = boardRows(crm).some(d => aiScoreOf(d) !== undefined);
  const stageKey = stages.join('|');
  const columns = useMemo(
    () => pipelineColumns({ stages, dateFmt: S.dateFmt, fmtMoney, act, withScores }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stageKey, S.dateFmt, fmtMoney, withScores]
  );

  const count = g => g.deals.length;
  const tabs = (
    <div className="pctabs" role="tablist" aria-label="Stage">
      {[['All', groups.reduce((t, g) => t + count(g), 0)], ...groups.map(g => [g.stage, count(g)])].map(([name, n]) => (
        <button
          key={name}
          type="button"
          role="tab"
          aria-selected={name === tab}
          className={`pctab${name === tab ? ' on' : ''}${n ? '' : ' none'}${name === 'All' ? ' all' : ''}`}
          onClick={() => layout.set({ tab: name })}
        >
          {name}
          <em>{n}</em>
        </button>
      ))}
    </div>
  );

  return (
    <DataGrid
      prefs={layout}
      columns={columns}
      data={data}
      getRowId={r => r.d.id}
      onRowClick={r => act.current.view(r.d)}
      toolbar={hideTabs ? null : tabs}
      viewKey={tab}
      label="Pipeline"
      exportName="xyra-pipeline"
      exportSheet="Pipeline"
      canExport={exportAllowed(me, menus)}
      dateFmt={S.dateFmt}
      emptyState={(
        <EmptyState icon="zap" tone="g-green" title={tab === 'All' ? 'No demo requests match' : `Nothing at ${tab}`}>
          {tab === 'All' ? 'Clear the filters, or wait for the tracker.' : 'Pick another stage, or clear the filters.'}
        </EmptyState>
      )}
    />
  );
}
